import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Panel } from '@appkit4/react-components/panel';
import { Button } from '@appkit4/react-components/button';
import { Switch } from '@appkit4/react-components/switch';
import styles from '../../../styles/DiffViewer.module.scss';
import { ChevronDownIcon, ChevronUpIcon } from '../../utility/Icons/Icons';
import {
  NavigationButtonsProps,
  DiffViewerProps,
  Difference,
  ProcessedContent,
  LineContent

 } from '../../../types/diffViewer';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

interface AutoSizerDimensions {
  height: number;
  width: number;
}

// Constants for virtualization
const LINE_HEIGHT = 24; // Height of each line in pixels
const OVERSCAN_COUNT = 40; // Number of items to render outside of the visible area

const Row = React.memo(({ 
  index, 
  style, 
  data: { 
    lines, 
    processLine, 
    isOriginal,
    setLineRef  // We'll add this to handle line references
  } 
}: {
  index: number;
  style: React.CSSProperties;
  data: {
    lines: LineContent[];
    processLine: (line: LineContent, isOriginal: boolean) => JSX.Element;
    isOriginal: boolean;
    setLineRef: (lineNumber: number, element: HTMLDivElement | null) => void;
  };
}) => {
  const line = lines[index];
  if (!line) return null;

  return (
   <div style={{...style, width: 'max-content', minWidth: '100%'}}>
      {processLine(line, isOriginal)}
    </div>
  );
});

const DiffViewer: React.FC<DiffViewerProps> = ({
  originalContent,
  modifiedContent,
  differences,
  scrollToLineRef,
}) => {
  const [verticalSync] = useState(true);
  const [horizontalSync, setHorizontalSync] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentDiffIndex, setCurrentDiffIndex] = useState<number>(-1);
  const [currentPDFPage, setCurrentPDFPage] = useState<number>(1);
  const lineRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const originalListRef = useRef<List>(null);
  const modifiedListRef = useRef<List>(null);
  // Add these new refs at the top of your component
  const originalScrollContainerRef = useRef<HTMLDivElement>(null);
  const modifiedScrollContainerRef = useRef<HTMLDivElement>(null);

    // Add this state

  // Add this effect at the top of your effects
  useEffect(() => {
    // Force toggle to trigger initialization
    setHorizontalSync(false);
    setTimeout(() => {
      setHorizontalSync(true);
    }, 0);
  }, []);

  
  const setLineRef = useCallback((lineNumber: number, element: HTMLDivElement | null) => {
    if (element) {
      lineRefs.current.set(lineNumber, element);
    } else {
      lineRefs.current.delete(lineNumber);
    }
  }, []);
  
// *** IMPORTANT: Define getPageForLine at the top level ***
const getPageForLine = useCallback((lineNumber: number): number => {
  let currentLine = 0;
  for (let i = 0; i < originalContent.length; i++) {
    const pageLines = originalContent[i].split('\n').length;
    if (currentLine + pageLines > lineNumber) {
      return i + 1;
    }
    currentLine += pageLines;
  }
  return originalContent.length;
}, [originalContent]);

// *** IMPORTANT: Define processContent at the top level (not nested inside any other function) ***
const processContent = useCallback((): ProcessedContent => {
  // 1. Prepare the raw lines from each page
  const rawOriginalLines: string[] = [];
  const rawModifiedLines: string[] = [];

  originalContent.forEach(page => {
    rawOriginalLines.push(...page.split('\n'));
  });
  modifiedContent.forEach(page => {
    rawModifiedLines.push(...page.split('\n'));
  });

  // 2. Create maps for differences with padding
  //    - For '+' diffs, we insert a placeholder in the original view.
  //    - For '-' diffs, we insert a placeholder in the modified view.
  const additionsMap = new Map<number, Difference[]>();
  const deletionsMap = new Map<number, Difference[]>();

  differences.forEach(diff => {
    if (diff.padding) {
      if (diff.action === '+') {
        if (!additionsMap.has(diff.row)) {
          additionsMap.set(diff.row, []);
        }
        additionsMap.get(diff.row)!.push(diff);
      } else if (diff.action === '-') {
        if (!deletionsMap.has(diff.row)) {
          deletionsMap.set(diff.row, []);
        }
        deletionsMap.get(diff.row)!.push(diff);
      }
    }
  });

  // 3. Build the final arrays by applying padding
  const originalLines: LineContent[] = [];
  const modifiedLines: LineContent[] = [];
  let origIndex = 0;
  let modIndex = 0;

  while (origIndex < rawOriginalLines.length || modIndex < rawModifiedLines.length) {
    // Handle additions: if at the current modified index there’s an extra line,
    // insert a placeholder in the original view.
    if (modIndex < rawModifiedLines.length && additionsMap.has(modIndex)) {
      originalLines.push({
        text: '',
        lineNumber: origIndex, // assign a sequential line number
        isPlaceholder: true,
      });
      modifiedLines.push({
        text: rawModifiedLines[modIndex],
        lineNumber: modIndex,
      });
      modIndex++;
      continue;
    }

    // Handle deletions: if at the current original index there’s an extra line,
    // insert a placeholder in the modified view.
    if (origIndex < rawOriginalLines.length && deletionsMap.has(origIndex)) {
      originalLines.push({
        text: rawOriginalLines[origIndex],
        lineNumber: origIndex,
      });
      modifiedLines.push({
        text: '',
        lineNumber: modIndex,
        isPlaceholder: true,
      });
      origIndex++;
      continue;
    }

    // Otherwise, add real lines from both arrays
    if (origIndex < rawOriginalLines.length) {
      originalLines.push({
        text: rawOriginalLines[origIndex],
        lineNumber: origIndex,
      });
      origIndex++;
    }
    if (modIndex < rawModifiedLines.length) {
      modifiedLines.push({
        text: rawModifiedLines[modIndex],
        lineNumber: modIndex,
      });
      modIndex++;
    }
  }

  return { originalLines, modifiedLines };
}, [originalContent, modifiedContent, differences]);

  const groupedDiffs = useMemo(() => {
    // Raggruppa differenze per riga
    const diffMap = differences.reduce((acc, diff) => {
      if (!acc[diff.row]) {
        acc[diff.row] = diff; // Aggiungi una differenza unica per riga
      }
      return acc;
    }, {} as Record<number, Difference>);
  
    return Object.values(diffMap); // Restituisci un array di differenze uniche
  }, [differences]);
  
  const totalDiffGroups = groupedDiffs.length; // Numero totale di gruppi unici


const NavigationButtons: React.FC<NavigationButtonsProps> = ({
  currentDiffIndex,
  totalDiffs,
  currentPage,
  onPrevClick,
  onNextClick
}) => {
  const scrollToTop = () => {
    if (originalListRef.current) {
      originalListRef.current.scrollTo(0);
    }
    if (modifiedListRef.current) {
      modifiedListRef.current.scrollTo(0);
    }
  };


  return (
    <div className={styles.floatingNavigation}>
      <div className={styles.navInfo}>
        {currentDiffIndex >= 0 && (
          <span style={{ fontSize: '16px' }}>Change {currentDiffIndex + 1} of {totalDiffs}</span>
        )}
        <span style={{ fontSize: '16px' }}>Page {currentPage}</span>
      </div>
      <div className={styles.navButtons}>
        <Button
          kind="secondary"
          compact
          onClick={scrollToTop}
          style={{ 
            paddingLeft: '16px',    // Add horizontal padding
            paddingRight: '16px'    // to create space around the text
          }}
        >
          Scroll Top
        </Button>
        <Button
          kind="secondary"
          onClick={onPrevClick}
          disabled={currentDiffIndex <= 0}
        >
          <ChevronUpIcon />
        </Button>
        <Button
          kind="secondary"
          onClick={onNextClick}
          disabled={currentDiffIndex >= totalDiffs - 1}
        >
          <ChevronDownIcon />
        </Button>
      </div>
    </div>
  );
};

  const processLine = useCallback((
    line: LineContent,
    isOriginal: boolean
  ): JSX.Element => {
    if (line.isPlaceholder) {
      return (
        <div 
          key={`placeholder-${line.lineNumber}`}
          className={`${styles.line} ${styles.placeholderLine}`}
          data-line-number={line.lineNumber}
          data-pdf-page={getPageForLine(line.lineNumber)}
        >
          <span className={styles.lineNumber}></span>
          <span className={styles.placeholderContent}></span>
        </div>
      );
    }

    const diff = differences.find(d => 
      d.row === line.lineNumber && 
      ((isOriginal && d.action === '-') || (!isOriginal && d.action === '+'))
    );

    if (!diff) {
      return (
        <div 
          ref={el => {
            if (el) lineRefs.current.set(line.lineNumber, el);
          }}
          key={`line-${line.lineNumber}`}
          className={styles.line}
          data-line-number={line.lineNumber}
          data-pdf-page={getPageForLine(line.lineNumber)}
        >
          <span className={styles.lineNumber}>{line.lineNumber}</span>
          <span className={styles.lineContent}>{line.text}</span>
        </div>
      );
    }

    if (!diff.range || diff.range.length === 0) {
      const diffClass = isOriginal ? styles.removedLineLight : styles.addedLineLight;
      return (
        <div 
          ref={el => {
            if (el) lineRefs.current.set(line.lineNumber, el);
          }}
          key={`line-${line.lineNumber}`}
          className={`${styles.line} ${styles.changedLine}`}
          data-line-number={line.lineNumber}
          data-pdf-page={getPageForLine(line.lineNumber)}
        >
          <span className={styles.lineNumber}>{line.lineNumber}</span>
          <span className={`${styles.lineContent} ${diffClass}`}>{line.text}</span>
        </div>
      );
    }

    // Handle inline differences
    const parts: JSX.Element[] = [];
    let lastIndex = 0;

    diff.range.forEach(([start, end], index) => {
      if (start > lastIndex) {
        parts.push(
          <span key={`normal-${index}`}>
            {line.text.slice(lastIndex, start)}
          </span>
        );
      }

      const highlightClass = isOriginal ? styles.removedHighlight : styles.addedHighlight;
      parts.push(
        <span key={`diff-${index}`} className={highlightClass}>
          {line.text.slice(start, end)}
        </span>
      );

      lastIndex = end;
    });

    if (lastIndex < line.text.length) {
      parts.push(<span key="normal-end">{line.text.slice(lastIndex)}</span>);
    }

    const baseClass = isOriginal ? styles.removedLineLight : styles.addedLineLight;
    return (
      <div 
        ref={el => {
          if (el) lineRefs.current.set(line.lineNumber, el);
        }}
        key={`line-${line.lineNumber}`}
        className={`${styles.line} ${styles.changedLine}`}
        data-line-number={line.lineNumber}
        data-pdf-page={getPageForLine(line.lineNumber)}
      >
        <span className={styles.lineNumber}>{line.lineNumber}</span>
        <span className={`${styles.lineContent} ${baseClass}`}>{parts}</span>
      </div>
    );
  }, [differences, getPageForLine]);


  const scrollToLine = useCallback((lineNumber: number) => {
    const containerHeight = originalListRef.current?.props.height || 0;
    const oneThirdHeight = Math.floor(Number(containerHeight) / 3);
    const scrollOffset = (lineNumber * LINE_HEIGHT) - oneThirdHeight;
    
    if (originalListRef.current) {
      originalListRef.current.scrollTo(Math.max(0, scrollOffset));
    }
    if (modifiedListRef.current) {
      modifiedListRef.current.scrollTo(Math.max(0, scrollOffset));
    }
  
    const element = lineRefs.current.get(lineNumber);
    if (element) {
      element.style.transition = 'background-color 0.3s ease-in-out';
      element.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 1000);
    }
  }, []);

  const findNextDiff = useCallback((direction: 'next' | 'prev') => {
    if (groupedDiffs.length === 0) return;
  
    let newIndex;
    if (currentDiffIndex === -1) {
      newIndex = direction === 'next' ? 0 : groupedDiffs.length - 1;
    } else {
      newIndex = direction === 'next'
        ? Math.min(currentDiffIndex + 1, groupedDiffs.length - 1)
        : Math.max(currentDiffIndex - 1, 0);
    }
  
    setCurrentDiffIndex(newIndex);
    scrollToLine(groupedDiffs[newIndex].row);
  }, [currentDiffIndex, groupedDiffs, scrollToLine]);

  useEffect(() => {
    if (scrollToLineRef) {
      scrollToLineRef.current = scrollToLine;
    }
  }, [scrollToLine, scrollToLineRef]);


  useEffect(() => {
    const handleScrolling = () => {
      const visibleElements = Array.from(lineRefs.current.entries())
        .filter(([_, element]) => {
          const rect = element.getBoundingClientRect();
          return rect.top >= 0 && rect.bottom <= window.innerHeight;
        });

      if (visibleElements.length > 0) {
        const middleElement = visibleElements[Math.floor(visibleElements.length / 2)];
        const page = Number(middleElement[1].getAttribute('data-pdf-page'));
        if (page !== currentPDFPage) {
          setCurrentPDFPage(page);
        }
      }
    };

    window.addEventListener('scroll', handleScrolling);
    return () => window.removeEventListener('scroll', handleScrolling);
  }, [currentPDFPage]);


  
useEffect(() => {
  if (!isInitialized && horizontalSync) {
    const originalContainer = originalScrollContainerRef.current;
    const modifiedContainer = modifiedScrollContainerRef.current;
    
    if (originalContainer && modifiedContainer) {
      modifiedContainer.scrollLeft = originalContainer.scrollLeft;
      setIsInitialized(true);
    }
  }
}, [isInitialized, horizontalSync]);

useEffect(() => {
  const originalContainer = originalScrollContainerRef.current;
  const modifiedContainer = modifiedScrollContainerRef.current;
  let isHandlingScroll = false;

  const handleScroll = (event: Event) => {
    if (!horizontalSync || isHandlingScroll) return;
    
    isHandlingScroll = true;
    const source = event.target as HTMLDivElement;
    const target = source === originalContainer ? modifiedContainer : originalContainer;

    if (target) {
      target.scrollLeft = source.scrollLeft;
    }

    isHandlingScroll = false;
  };

  if (originalContainer && modifiedContainer) {
    originalContainer.addEventListener('scroll', handleScroll, { passive: true });
    modifiedContainer.addEventListener('scroll', handleScroll, { passive: true });
  }

  return () => {
    originalContainer?.removeEventListener('scroll', handleScroll);
    modifiedContainer?.removeEventListener('scroll', handleScroll);
  };
}, [horizontalSync, isInitialized]);

  // Update the scroll handler with proper typing
  const handleVirtualizedScroll = useCallback(({ 
    scrollOffset, 
    scrollUpdateWasRequested 
  }: {
    scrollOffset: number;
    scrollUpdateWasRequested: boolean;
  }) => {
    if (!verticalSync || scrollUpdateWasRequested) return;
  
    if (originalListRef.current) {
      originalListRef.current.scrollTo(scrollOffset);
    }
    if (modifiedListRef.current) {
      modifiedListRef.current.scrollTo(scrollOffset);
    }
  }, [verticalSync]);

  const { originalLines, modifiedLines } = processContent();

  

  return (
    <Panel className={styles.diffViewer}>
        <div style={{ display: 'flex', gap: '20px', marginTop:'5px' }}>
          <Switch
            id="horizontal-sync-switch"
            checked={horizontalSync}
            onChange={setHorizontalSync}
            showIndicator={true}
          >
            Scroll orizzontale
          </Switch>
        </div>

      <div className={styles.diffContainer}>
        <AutoSizer>
          {({ height, width }: AutoSizerDimensions) => (
            <div style={{ display: 'flex', width: width, height: height }}>
              <div className={styles.diffColumn} style={{ width: '50%' }}>
                <h3>Original</h3>
                <List
                  ref={originalListRef}
                  outerRef={originalScrollContainerRef}
                  height={height - 40}
                  width={width / 2}
                  itemCount={originalLines.length}
                  itemSize={LINE_HEIGHT}
                  overscanCount={OVERSCAN_COUNT}
                  onScroll={handleVirtualizedScroll}
                  className={styles.virtualizedList}
                  itemData={{
                    lines: originalLines,
                    processLine,
                    isOriginal: true,
                    setLineRef
                  }}
                >
                  {Row}
                </List>
              </div>

              <div className={styles.diffColumn} style={{ width: '50%' }}>
                <h3>Modified</h3>
                <List
                  ref={modifiedListRef}
                  outerRef={modifiedScrollContainerRef}
                  height={height - 40}
                  width={width / 2}
                  itemCount={modifiedLines.length}
                  itemSize={LINE_HEIGHT}
                  overscanCount={OVERSCAN_COUNT}
                  onScroll={handleVirtualizedScroll}
                  className={styles.virtualizedList}
                  itemData={{
                    lines: modifiedLines,
                    processLine,
                    isOriginal: false,
                    setLineRef
                  }}
                >
                  {Row}
                </List>
              </div>
            </div>
          )}
        </AutoSizer>
      </div>

      <NavigationButtons
        currentDiffIndex={currentDiffIndex}
        totalDiffs={totalDiffGroups}
        currentPage={currentPDFPage}
        onPrevClick={() => findNextDiff('prev')}
        onNextClick={() => findNextDiff('next')}
        originalListRef={originalListRef}
        modifiedListRef={modifiedListRef}
      />
    </Panel>
  );
}
export default DiffViewer;
